import {
  featureTypes,
  featureSource,
  Patch,
  RevegetationArea,
  Tree,
  treeType,
  treeSize,
  nvrTools,
  treeCmType,
} from "../lib/types";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import Multipoint from "@arcgis/core/geometry/Multipoint";
import Geometry from "@arcgis/core/geometry/Geometry";
import Graphic from "@arcgis/core/Graphic";
import mapConfig, {
  geometryServiceUrl,
  mapImageLayerName,
} from "../lib/mapConfig";
import GraphicsLayer from "esri/layers/GraphicsLayer";
import Map from "@arcgis/core/Map";
import Sublayer from "esri/layers/support/Sublayer";
import Query from "esri/rest/support/Query";
import * as query from "@arcgis/core/rest/query.js";
import * as geometryService from "@arcgis/core/rest/geometryService.js";
import AreasAndLengthsParameters from "@arcgis/core/rest/support/AreasAndLengthsParameters.js";
import BufferParameters from "@arcgis/core/rest/support/BufferParameters.js";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { largeSTCondScore, mediumSTCondScore } from "../lib/constants";

const initialIndices = {
  [featureTypes.patch]: "1",
  [featureTypes.tree]: "A",
  [featureTypes.revegetationArea]: "R1",
};

export const getFeatureSourcesCount = (
  features: Record<string, Patch | Tree | RevegetationArea>
) => {
  const drawnCount = Object.values(features).some(
    (x: any) => x.source === featureSource.Drawn
  )
    ? 1
    : 0;
  const uploadedCount = Object.values(features).some(
    (x: any) => x.source === featureSource.Uploaded
  )
    ? 1
    : 0;
  return drawnCount + uploadedCount;
};

export const reorderIds = (
  ids: string[],
  featureType: featureTypes
): Record<string, string> => {
  let currentIndex = initialIndices[featureType];
  const reOrdered: Record<string, string> = {};
  ids.forEach((x) => {
    reOrdered[x] = currentIndex;
    currentIndex = getNextFeatureId(Object.values(reOrdered), featureType);
  });
  return reOrdered;
};

export const getNextFeatureId = (ids: string[], featureType: featureTypes) => {
  const operation = {
    [featureTypes.patch]: nextNumber,
    [featureTypes.tree]: nextChar,
    [featureTypes.revegetationArea]: nextPrefixedNumber,
  }[featureType];
  const initialValue = initialIndices[featureType];
  let nextValue: any = initialValue;
  while (ids.includes(nextValue)) {
    nextValue = operation(nextValue);
  }
  return nextValue;
};

const nextNumber = (n: string) => {
  return String(Number(n) + 1);
};

const nextPrefixedNumber = (n: string) => {
  const numberPart = n.slice(1);
  return "R" + nextNumber(numberPart);
};

const nextChar = (c: string) => {
  var u = c.toUpperCase();
  if (u === "Z") {
    var txt = "";
    var i = u.length;
    while (i--) {
      txt += "A";
    }
    return txt + "A";
  } else {
    var p = "";
    var q = "";
    if (u.length > 1) {
      p = u.substring(0, u.length - 1);
      q = String.fromCharCode(p.slice(-1).charCodeAt(0));
    }
    var l = u.slice(-1).charCodeAt(0);
    var z = nextLetter(l);
    if (z === "A") {
      return p.slice(0, -1) + nextLetter(q.slice(-1).charCodeAt(0)) + z;
    } else {
      return p + z;
    }
  }
};

const nextLetter = (l: number) => {
  if (l < 90) {
    return String.fromCharCode(l + 1);
  } else {
    return "A";
  }
};

export const getAreasAndLengths = async (
  polygon: Polygon,
  areaUnit: any = "hectares",
  lengthUnit: any = "meters"
) => {
  const result = await geometryService.areasAndLengths(
    geometryServiceUrl,
    new AreasAndLengthsParameters({
      areaUnit: areaUnit,
      lengthUnit: lengthUnit,
      calculationType: "geodesic",
      polygons: [polygon],
    })
  );
  return result;
};

export const buffer = async (features: any[], distance: number) => {
  const result = await geometryService.buffer(
    geometryServiceUrl,
    new BufferParameters({
      distances: [distance],
      geodesic: false,
      geometries: features,
      unit: "meters",
    })
  );
  return result;
};

export const getArea = async (polygon: Polygon) => {
  const result = await getAreasAndLengths(polygon);
  return result.areas[0];
};

export const getPolygonlabel = async (
  polygon: Polygon,
  text: string
): Promise<Graphic> => {
  const labelGeom: any = await geometryService.labelPoints(geometryServiceUrl, [
    polygon,
  ]);
  const labelGraphic = new Graphic({
    attributes: { id: text },
    geometry: labelGeom[0],
    symbol: {
      text: text,
      ...mapConfig.drawSymbols.polygonLabel,
    },
  });
  return labelGraphic;
};

export const getPointLabel = (
  point: Point,
  text: string,
  color: string
): Graphic => {
  const symbolSettings = mapConfig.drawSymbols.treeLabelDefault;
  symbolSettings.text = text;
  symbolSettings.color = color;
  const labelGraphic = new Graphic({
    attributes: { id: text },
    geometry: point,
    symbol: symbolSettings,
  });
  return labelGraphic;
};

export const getPointBuffer = async (
  feature: Tree,
  toolMode: nvrTools
): Promise<Graphic> => {
  let symbolSettings =
    mapConfig.drawSymbols[
      feature.treeType === treeType.Patch ? "patch" : "scatteredTreeBuffer"
    ];
  if (feature.benchmarkCm === undefined) {
    symbolSettings = mapConfig.drawSymbols.flaggedTreeBuffer;
  }

  // Scattered trees in the offset tool are always represented as large trees as only
  // trees that are > 75% of the Large Tree Benchmark value are allowed to be mapped.
  const distance =
    toolMode === nvrTools.offset && feature.treeType === treeType.Scattered
      ? mapConfig.treeBuffers[treeSize.Lg]
      : mapConfig.treeBuffers[feature.size || treeSize.Sm];
  const result: any = await buffer([new Point(feature.geom)], distance);
  const bufferGraphic = new Graphic({
    attributes: { id: feature.id },
    geometry: result[0],
    symbol: symbolSettings,
  });
  return bufferGraphic;
};

export const polygonHasAtLeastOneDrawnVertex = (polygon: Polygon) => {
  const polygonRing = polygon.rings[0];
  return polygonRing.length > 2;
};

export const isPolygonInvalid = (polygon: Polygon) => {
  return polygon.isSelfIntersecting;
};

export const doesGeometryIntersectMapLayer = async (
  geom: Point | Polygon | Multipoint,
  map: Map,
  layerId: number,
  spatialRelationship?: string
) => {
  const layer = map.findLayerById(mapImageLayerName) as any;
  const sublayer = layer.sublayers.items.filter(
    (x: Sublayer) => x.id === layerId
  )[0];
  const query: Query = sublayer.createQuery();
  query.geometry = geom;
  query.spatialRelationship = (spatialRelationship || "intersects") as any;
  query.outFields = ["OBJECTID"];
  query.returnGeometry = false;
  const result = await sublayer.queryFeatures(query);
  return result.features.length > 0;
};

export const getOverlappingFeatures = (
  geom: Point | Polygon,
  features: any[]
) => {
  const overlappingPolygons: string[] = [];
  features.forEach((x) => {
    if (geometryEngine.overlaps(x.geom as Point | Polygon, geom)) {
      overlappingPolygons.push(x.id);
    }
  });
  return overlappingPolygons;
};

export const doesGeometryOverlapGeometries = (geom: any, geoms: any[]) => {
  const overlappingGeoms = getOverlappingFeatures(
    geom,
    geoms.map((x: any) => ({ geom: x }))
  );
  return !!overlappingGeoms.length;
};

const queryEVC = async (geom: Point | Polygon, map: Map) => {
  const layer = map.findLayerById(mapImageLayerName) as any;
  const evcLayer = layer.sublayers.items.filter(
    (x: Sublayer) => x.id === mapConfig.mapServiceConfig.evcLayerId
  )[0];
  const query: Query = evcLayer.createQuery();
  query.geometry = geom;
  query.outFields = [mapConfig.mapServiceConfig.evcLayerCodeField];
  query.returnGeometry = false;
  const result = await evcLayer.queryFeatures(query);
  const features = result.features;
  if (features.length) {
    return features.map(
      (x: any) => x.attributes[mapConfig.mapServiceConfig.evcLayerCodeField]
    );
  }
};

const queryBenchmarkTable = async (evcList: string[]) => {
  const tableConfig = mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark;
  const queryUrl = `${mapConfig.mapServiceConfig.url}/${tableConfig.layerId}`;
  const outFields = [
    tableConfig.codeField,
    tableConfig.woodyField,
    tableConfig.speciesField,
    tableConfig.benchmarkField,
  ];
  const result = await query.executeQueryJSON(queryUrl, {
    where: `${
      mapConfig.mapServiceConfig.tables.evcLargeTreeBenchmark.codeField
    } IN ('${evcList.join(", ")}')`,
    outFields: outFields,
  });
  const features = result.features;
  if (features.length) {
    const response = features.map((x: any) => {
      let y = x.attributes;
      let response: any = {};
      response[tableConfig.codeField] =
        y[tableConfig.codeField] || y[tableConfig.codeField.toLowerCase()];
      response[tableConfig.woodyField] =
        y[tableConfig.woodyField] || y[tableConfig.woodyField.toLowerCase()];
      response[tableConfig.speciesField] =
        y[tableConfig.speciesField] ||
        y[tableConfig.speciesField.toLowerCase()];
      response[tableConfig.benchmarkField] =
        y[tableConfig.benchmarkField] ||
        y[tableConfig.benchmarkField.toLowerCase()];
      return response;
    });
    return response;
  } else return [];
};

export const getLTBenchmarks = async (geom: Point | Polygon, map: Map) => {
  const evcList = await queryEVC(geom, map);
  let ltBenchmarks: any[] = [];

  if (evcList) {
    const uniqueEvcList: string[] = Array.from(new Set(evcList));
    ltBenchmarks = await queryBenchmarkTable(uniqueEvcList);
    ltBenchmarks = ltBenchmarks.flat();
  }
  return {
    evc: evcList ? evcList[0] : undefined,
    ltBenchmarks,
  };
};

export const getLTBenchmarksFromEvc = async (evc: string) => {
  let ltBenchmarks = [];
  ltBenchmarks = await queryBenchmarkTable([evc]);
  ltBenchmarks = ltBenchmarks.flat();
  return {
    evc,
    ltBenchmarks,
  };
};

export const filterBenchmarksBasedOnConditionScore = (
  benchmarks: any[],
  treeProperties: any
) => {
  const cmType =
    treeProperties.meas_type.toLowerCase() === "cir"
      ? treeCmType.CIR
      : treeCmType.DBH;
  const cm = treeProperties.meas;
  const treeCIR = cmType === treeCmType.CIR ? Number(cm) : Number(cm) * Math.PI;
  const condScore = treeProperties.cond_score;
  const newBenchmarks = benchmarks.filter((x) => {
    if (condScore === mediumSTCondScore) {
      return 0.75 * x.LTBM_NUM < treeCIR && treeCIR < x.LTBM_NUM;
    }
    if (condScore === largeSTCondScore) {
      return treeCIR >= x.LTBM_NUM;
    }
    return false;
  });
  return newBenchmarks;
};

export const mergePolygons = (polygons: Polygon[]) => {
  return geometryEngine.union(polygons);
};

export const graphicExistsInGraphicsLayer = (
  graphic: Graphic,
  graphicsLayer: GraphicsLayer
) => {
  return graphicsLayer.graphics.toArray().some((x) => {
    return x.attributes && x.attributes.id === graphic.attributes.id;
  });
};

export const getIntersectingPoints = (
  points: Record<string, Point>,
  polygon: Polygon
): string[] => {
  const intersecting: string[] = [];
  Object.keys(points).forEach((x) => {
    const point = points[x];
    if (geometryEngine.intersects(point, polygon)) {
      intersecting.push(x);
    }
  });
  return intersecting;
};

export const projectGeometries = async (
  geometries: Geometry[],
  wkid: number
): Promise<Geometry[]> => {
  if (!geometries.length) {
    return [];
  }
  const sr = new SpatialReference({ wkid });
  const projectedGeoms: any = await geometryService.project(
    geometryServiceUrl,
    {
      geometries,
      outSpatialReference: sr,
    } as any
  );
  return projectedGeoms;
};

export const getAreaToPerimeterRatio = async (polygon: Polygon) => {
  const areasAndLengths = await getAreasAndLengths(
    polygon,
    "square-meters",
    "meters"
  );
  const area = areasAndLengths.areas[0];
  const perimeter = areasAndLengths.lengths[0];
  const areaToPerimeterRatio = area / perimeter;
  return areaToPerimeterRatio;
};
